// src/components/HomePage.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Header from './Header';
import TalentModal from './TalentModal';
import EventCalendar from './EventCalendar';
import EventModal from './EventModal';
import NoEventModal from './NoEventModal';
import '../css/HomePage.css';
import ContactCustomerService from './ContactCustomerService';
import PreOnboardingModal from './PreOnboardingModal';
import OnboardingModal from './OnboardingModal';
import DeploymentModal from './DeploymentModal';
import OffboardingModal from './OffboardingModal';
import FullSuiteModal from './FullSuiteModal';
import ProjectModal from './ProjectModal';
import ContactHQModal from './ContactHQModal';
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext';
import welcomeMessageManager from '../utils/welcomeMessageManager';

// ✅ Live APIs
import { TalentsAPI } from '../data/talent';
import { BroadcastsAPI } from '../data/broadcasts';
import { getShortlist } from '../data/shortlist';

const STAGES = ['Shortlist', 'Pre-Onboarding', 'Onboarding', 'Deployment', 'Offboarding'];
const LIFECYCLE_STATUSES = {
  Shortlist: [],
  'Pre-Onboarding': [
    'Talent Sourcing & Pooling',
    'Screening & Assessments',
    'Structured Interviews',
    'Client Interviews',
  ],
  Onboarding: [
    'Background Checks & Verification',
    'Medical Screening',
    'Employment Documents',
    'Work Enablement / Workplace Setup',
  ],
  Deployment: ['Training', 'Nesting', 'Go-Live'],
  Offboarding: ['Pre-Offboarding', 'Offboarding', 'Post-Offboarding'],
};

// Normalize stage coming from DB (supports index "0..4", exact names, or case variants)
function normalizeStage(raw) {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (/^\d+$/.test(s)) {
    const idx = Math.max(0, Math.min(STAGES.length - 1, Number(s)));
    return STAGES[idx];
  }
  const exact = STAGES.find((x) => x === s);
  if (exact) return exact;
  const loose = STAGES.find((x) => x.toLowerCase() === s.toLowerCase());
  return loose || null;
}

// Extract rows from either an array payload or { ok, rows: [{ talents: {...}}] }
function coerceTalentRows(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload && payload.ok && Array.isArray(payload.rows)) {
    return payload.rows.map((r) => r.talents || r);
  }
  return [];
}

function HomePage() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [selectedTalent, setSelectedTalent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // User verification state
  const [userData, setUserData] = useState({
    isVerified: false,
    isLoading: true
  });

  // Action Modals
  const [isPreOnboardingOpen, setIsPreOnboardingOpen] = useState(false);
  const [isOnboardingOpen, setIsOnboardingOpen] = useState(false);
  const [isDeploymentOpen, setIsDeploymentOpen] = useState(false);
  const [isOffboardingOpen, setIsOffboardingOpen] = useState(false);
  const [isFullSuiteOpen, setIsFullSuiteOpen] = useState(false);
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [isContactHQOpen, setIsContactHQOpen] = useState(false);
  
  // Verification modal state
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [showVerificationPrompt, setShowVerificationPrompt] = useState(false);
  const [verificationAction, setVerificationAction] = useState(null);
  
  // Login verification prompt modal state
  const [showLoginVerificationModal, setShowLoginVerificationModal] = useState(false);
  const [hasShownLoginPrompt, setHasShownLoginPrompt] = useState(false);

  // Event Calendar Modals
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [isNoEventModalOpen, setIsNoEventModalOpen] = useState(false);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);


  const [activeLifecycleStage, setActiveLifecycleStage] = useState(null);

  // Time header
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const timeStr = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const dateStr = now.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });

  // Global welcome message state (persists across page navigation)
  const [currentMessageIndex, setCurrentMessageIndex] = useState(() => welcomeMessageManager.getCurrentIndex());
  const [currentWelcomeMessage, setCurrentWelcomeMessage] = useState(() => welcomeMessageManager.getCurrentMessage());

  // Get user display name function
  const getUserDisplayName = useCallback(() => {
    if (currentUser && currentUser.displayName) {
      return currentUser.displayName;
    }
    return "Guest User"; // Fallback for when user is not logged in
  }, [currentUser]);

  // Get broadcast icon based on type
  const getBroadcastIcon = (type) => {
    const icons = {
      'announcement': '📢',
      'news': '📰',
      'update': '🔄',
      'event': '🎉',
      'warning': '⚠️',
      'info': 'ℹ️',
      'default': '📡'
    };
    return icons[type?.toLowerCase()] || icons.default;
  };

  // Set initial welcome message with user's name
  useEffect(() => {
    setCurrentWelcomeMessage(`Welcome back, ${getUserDisplayName()} - let's build your team today!`);
  }, [currentUser, getUserDisplayName]);

  // Subscribe to global welcome message updates
  useEffect(() => {
    const unsubscribe = welcomeMessageManager.subscribe((newMessage, newIndex) => {
      setCurrentWelcomeMessage(newMessage);
      setCurrentMessageIndex(newIndex);
    });

    return unsubscribe;
  }, []);

  // ✅ Load talents from shortlist table only
  const [rowsRaw, setRowsRaw] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const loadTalents = async () => {
    try {
      setLoading(true); setErr('');
      // Load all talents from shortlist table instead of talents table
      const shortlist = await getShortlist(currentUser?.uid);
      setRowsRaw(shortlist || []);
    } catch (e) {
      setErr(e?.message || String(e));
      setRowsRaw([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
      if (currentUser?.uid) {
      loadTalents(); 
    }
  }, [currentUser?.uid]);

  // 🔁 Build stage buckets from shortlist table only
  const stageBuckets = useMemo(() => {
    const buckets = Object.fromEntries(STAGES.map(s => [s, []]));
    
    // Add talents from shortlist table (all stages)
    (rowsRaw || []).forEach((r) => {
      const stage = normalizeStage(r?.stage || 'Shortlist');
      if (!stage) return;
      if (!STAGES.includes(stage)) return;

      buckets[stage].push({
        ...r,
        id: r.talent_id || r.talentId || r.id,
        name: r.talent_name || r.name,
        role: r.talent_role || r.role,
        stage,
        status: r.status,
        __raw: r,
      });
    });
    
    return buckets;
  }, [rowsRaw]);

  const stageCounts = useMemo(
    () => Object.fromEntries(STAGES.map(s => [s, stageBuckets[s]?.length || 0])),
    [stageBuckets]
  );

  const closeModal = () => { setIsModalOpen(false); setSelectedTalent(null); };

  // Event handlers
  const handleEventClick = (events, date) => {
    setSelectedEvents(events);
    setSelectedDate(date);
    
    if (events && events.length > 0) {
      setIsEventModalOpen(true);
      setIsNoEventModalOpen(false);
    } else {
      setIsNoEventModalOpen(true);
      setIsEventModalOpen(false);
    }
  };

  // Handle clicks on dates without events
  const handleEmptyDateClick = (date) => {
    setSelectedDate(date);
    setIsNoEventModalOpen(true);
    setIsEventModalOpen(false);
  };

  const closeEventModal = () => {
    setIsEventModalOpen(false);
    setSelectedEvents([]);
    setSelectedDate(null);
  };

  const closeNoEventModal = () => {
    setIsNoEventModalOpen(false);
    setSelectedDate(null);
  };

  /* =========================
     Broadcasts (from backend)
  ========================= */
  const [broadcasts, setBroadcasts] = useState([]);
  const [bLoading, setBLoading] = useState(true);
  const [bErr, setBErr] = useState('');
  const [selectedBroadcast, setSelectedBroadcast] = useState(null);
  const [isBroadcastModalOpen, setIsBroadcastModalOpen] = useState(false);
  const [dismissedBroadcasts, setDismissedBroadcasts] = useState(new Set());

  const openBroadcastModal = (broadcast) => { setSelectedBroadcast(broadcast); setIsBroadcastModalOpen(true); };
  const closeBroadcastModal = () => { 
    if (selectedBroadcast) {
      const broadcastId = selectedBroadcast.id || selectedBroadcast.ROWID || selectedBroadcast.title;
      setDismissedBroadcasts(prev => new Set([...prev, broadcastId]));
    }
    setIsBroadcastModalOpen(false); 
    setSelectedBroadcast(null); 
  };

  const displayDate = (post) => {
    const raw = post?.date || post?.datetime || post?.createdAt || post?.updatedAt;
    if (!raw) return '—';
    const d = new Date(raw);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Check if broadcast should be shown (event date >= current date)
  const shouldShowBroadcast = (post) => {
    const raw = post?.date || post?.datetime || post?.createdAt || post?.updatedAt;
    if (!raw) return false;
    const broadcastDate = new Date(raw);
    if (isNaN(broadcastDate.getTime())) return false;
    
    const now = new Date();
    // Show broadcasts where event date >= current date (today and future)
    return now <= broadcastDate;
  };

  // Check if broadcast should auto-open (datetime matches current date)
  const shouldAutoOpenBroadcast = (post) => {
    const raw = post?.date || post?.datetime || post?.createdAt || post?.updatedAt;
    if (!raw) return false;
    const broadcastDate = new Date(raw);
    if (isNaN(broadcastDate.getTime())) return false;
    
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const broadcastDay = new Date(broadcastDate.getFullYear(), broadcastDate.getMonth(), broadcastDate.getDate());
    
    return broadcastDay.getTime() === today.getTime();
  };

  const loadBroadcasts = async () => {
    try {
      setBLoading(true); setBErr('');
      const list = await BroadcastsAPI.list();
      const allBroadcasts = Array.isArray(list) ? list : coerceTalentRows(list);
      
      // Filter broadcasts to only show future/current ones
      const filteredBroadcasts = allBroadcasts.filter(shouldShowBroadcast);
      setBroadcasts(filteredBroadcasts);
      
      // Auto-open modal for broadcasts that match today's date and haven't been dismissed
      const todayBroadcast = allBroadcasts.find(broadcast => {
        const broadcastId = broadcast.id || broadcast.ROWID || broadcast.title;
        return shouldAutoOpenBroadcast(broadcast) && !dismissedBroadcasts.has(broadcastId);
      });
      if (todayBroadcast) {
        setTimeout(() => {
          setSelectedBroadcast(todayBroadcast);
          setIsBroadcastModalOpen(true);
        }, 1000); // Small delay to ensure UI is ready
      }
    } catch (e) {
      setBErr(e?.message || String(e));
      setBroadcasts([]);
    } finally {
      setBLoading(false);
    }
  };

  useEffect(() => {
    loadBroadcasts();
    
    // Set up periodic refresh to handle date changes
    const interval = setInterval(() => {
      loadBroadcasts();
    }, 60000); // Check every minute
    
    return () => clearInterval(interval);
  }, []);

  // Fetch user verification status
  const fetchUserData = async () => {
    if (!currentUser?.uid) {
      setUserData({ isVerified: false, isLoading: false });
      return;
    }

    try {
      setUserData(prev => ({ ...prev, isLoading: true }));
      
      const response = await fetch(`https://exodiauno-896735867.development.catalystserverless.com/server/exodia_uno_function/user?userId=${currentUser.uid}`);
      
      if (response.ok) {
        const result = await response.json();
        const user = result.data;
        
        setUserData({
          isVerified: user?.is_verified || user?.email_verified || false,
          isLoading: false
        });
      } else {
        setUserData({ isVerified: false, isLoading: false });
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      setUserData({ isVerified: false, isLoading: false });
    }
  };

  useEffect(() => {
    if (currentUser?.uid) {
      fetchUserData();
    }
  }, [currentUser?.uid]);

  // Show login verification modal for unverified users (once per login session)
  useEffect(() => {
    if (currentUser?.uid && !userData.isLoading && !userData.isVerified && !hasShownLoginPrompt) {
      setShowLoginVerificationModal(true);
      setHasShownLoginPrompt(true);
    }
  }, [currentUser?.uid, userData.isLoading, userData.isVerified, hasShownLoginPrompt]);

  // Reset login prompt flag when user logs out
  useEffect(() => {
    if (!currentUser?.uid) {
      setHasShownLoginPrompt(false);
    }
  }, [currentUser?.uid]);

  // Verification check function
  const checkVerificationAndProceed = (actionName, actionFunction) => {
    if (userData.isLoading) {
      // Show loading modal instead of alert
      setVerificationAction({ name: actionName, function: actionFunction });
      setShowVerificationPrompt(true);
      return;
    }

    if (!userData.isVerified) {
      // Show custom gamified modal instead of browser confirm
      setVerificationAction({ name: actionName, function: actionFunction });
      setShowVerificationPrompt(true);
      return;
    }

    // User is verified, proceed with the action
    actionFunction();
  };

  const StageCard = ({ stage }) => {
    const active = activeLifecycleStage === stage;
    return (
      <div
        className={`lifecycle-stage-card ${active ? 'active' : ''}`}
        onClick={() => setActiveLifecycleStage(active ? null : stage)}
      >
        {!active ? (
          <div className="stage-header">
            <div className="stage-info">
              <h3 className="stage-title">{stage}</h3>
              <span className="stage-count">({stageCounts[stage] || 0})</span>
            </div>
          </div>
        ) : (
          <div className="stage-details">
            <div className="stage-items">
              <div className="stage-item">
                <span className="item-name">
                  Talents in {stage}
                  <span className="item-count" style={{ marginLeft: 4, color: '#ff9100', fontWeight: 600 }}>
                    ({stageCounts[stage] || 0})
                  </span>
                </span>
              </div>
            </div>
            {Array.isArray(LIFECYCLE_STATUSES[stage]) && LIFECYCLE_STATUSES[stage].length > 0 && (
              <ul className="lifecycle-status-list" style={{ marginTop: '1rem', paddingLeft: '1.5rem' }}>
                {LIFECYCLE_STATUSES[stage].map((status, idx) => (
                  <li
                    key={idx}
                    style={{
                      marginBottom: '0.3rem',
                      color: '#ffffffff',
                      fontWeight: 500,
                      fontFamily: 'Orbitron, Segoe UI, sans-serif',
                      letterSpacing: '0.5px',
                    }}
                  >
                    {status}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    );
  };

  const StageGroups = ({ stage }) => {
    const items = stageBuckets[stage] || [];
    const countLabel = `${items.length} talent${items.length !== 1 ? 's' : ''}`;

    return (
      <div className="hiring-journey-grid">
        <div className="journey-category">
          <div className="category-header">
            <h3 className="category-title">{stage}</h3>
            <span className="candidate-count">{countLabel}</span>
          </div>
          <div className="candidates-list">
            <table className="stage-table" style={{ width: '100%', borderCollapse: 'collapse', marginTop: '1rem' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>ID</th>
                  <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>Name</th>
                  <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>Role</th>
                  <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>Stage</th>
                  <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>Status</th>
                  <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {items.length ? (
                  items.map((c) => (
                    <tr key={`${stage}-${c.id}`}>
                      <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>{c.id}</td>
                      <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>{c.name}</td>
                      <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>{c.role}</td>
                      <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>{c.stage}</td>
                      <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>{c.status}</td>
                      <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
                        <button
                          className="view-btn"
                          style={{ padding: '4px 12px', borderRadius: '6px', border: '1px solid #bbb', background: '#fff', cursor: 'pointer' }}
                          onClick={() => {
                            const talentId = c.talentId ?? c.id ?? c.ROWID;
                            // Send both: query params (for deep-link) and state (for modern usage)
                            navigate(`/squad-tracker?stage=${encodeURIComponent(stage)}&talentId=${encodeURIComponent(talentId)}`, {
                            state: { stage, highlightTalentId: talentId }
                            });
                          }}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', padding: '16px', color: '#888' }}>
                      No talents currently in this stage
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="homepage-container">
      <style>
        {`
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
          }
          @keyframes bounce {
            0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
            40% { transform: translateY(-10px); }
            60% { transform: translateY(-5px); }
          }
          @keyframes glow {
            0%, 100% { box-shadow: 0 0 5px rgba(102, 126, 234, 0.5); }
            50% { box-shadow: 0 0 20px rgba(102, 126, 234, 0.8), 0 0 30px rgba(102, 126, 234, 0.6); }
          }
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideInUp {
            from { 
              opacity: 0;
              transform: translateY(30px) scale(0.9);
            }
            to { 
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes float {
            0%, 100% { 
              transform: translateY(0px) translateX(0px) rotate(0deg);
              opacity: 0.7;
            }
            25% { 
              transform: translateY(-20px) translateX(10px) rotate(90deg);
              opacity: 1;
            }
            50% { 
              transform: translateY(-10px) translateX(-5px) rotate(180deg);
              opacity: 0.8;
            }
            75% { 
              transform: translateY(-30px) translateX(15px) rotate(270deg);
              opacity: 0.9;
            }
          }
        `}
      </style>
      <Header />

      <main className="homepage-main">
        {/* Welcome */}
        <section className="page-section" id="welcome">
          <div className="section-shell">
            <div className="welcome-grid">
              <div className="welcome-left">
                <h1 className="welcome-title">{currentWelcomeMessage}!</h1>
                {/* Debug button - remove in production */}
              </div>
              <div className="welcome-right">
                <div className="clock-time">{timeStr}</div>
                <div className="clock-date">{dateStr}</div>
                <div className="clock-tz">{timeZone}</div>
              </div>
            </div>
          </div>
        </section>

        {/* Quick Actions */}
        <section className="page-section" id="welcome">
          <div className="journey-actions-wrap">
            <div className="journey-actions journey-actions--four">
              <button className="action-btn start-pre" onClick={() => checkVerificationAndProceed('start Pre-Onboarding', () => setIsPreOnboardingOpen(true))} aria-label="Start Pre-Onboarding"><span className="btn-text">Start <br/> Pre-Onboarding</span></button>
              <button className="action-btn start-pre" onClick={() => checkVerificationAndProceed('start Onboarding', () => setIsOnboardingOpen(true))} aria-label="Start Onboarding"><span className="btn-text">Start<br/> Onboarding</span></button>
              <button className="action-btn start-pre" onClick={() => checkVerificationAndProceed('start Deployment', () => setIsDeploymentOpen(true))} aria-label="Start Deployment"><span className="btn-text">Start<br/> Deployment</span></button>
              <button className="action-btn start-pre" onClick={() => checkVerificationAndProceed('start Offboarding', () => setIsOffboardingOpen(true))} aria-label="Start Offboarding"><span className="btn-text">Start<br/> Offboarding</span></button>
            </div>
            <div className="journey-actions journey-actions--three">
              <button className="action-btn start-pre" onClick={() => checkVerificationAndProceed('start Full Suite', () => setIsFullSuiteOpen(true))} aria-label="Start Full Suite"><span className="btn-text">Start <br/> Full Suite</span></button>
              <button className="action-btn start-pre" onClick={() => checkVerificationAndProceed('start Project', () => setIsProjectModalOpen(true))} aria-label="Start Project"><span className="btn-text">Start <br/> Project</span></button>
              <button className="action-btn start-pre" onClick={() => checkVerificationAndProceed('contact HQ', () => setIsContactHQOpen(true))} aria-label="Contact HQ"><span className="btn-text">Contact <br/> HQ</span></button>
            </div>
          </div>
        </section>

        {/* Hiring Journey Progress (DB only) */}
        <section className="page-section" id="hiring-journey">
          <div className="section-header">
            <h2 className="section-title">Hiring Journey Progress</h2>
            <div style={{fontSize: 12, color: '#888'}}>{loading ? 'Loading…' : err ? `Error: ${err}` : ''}</div>
          </div>

          <div className="lifecycle-stages-container">
            <div className="lifecycle-stages-grid">
              {[STAGES[0], STAGES[1], STAGES[2]].map((stage, index) => (
                <React.Fragment key={stage}>
                  <StageCard stage={stage} />
                  {index < 2 && <div className="stage-arrow-connector">→</div>}
                </React.Fragment>
              ))}
            </div>
            <div className="lifecycle-stages-grid" style={{ marginTop: '1.5rem' }}>
              {[STAGES[3], STAGES[4]].map((stage, index) => (
                <React.Fragment key={stage}>
                  <StageCard stage={stage} />
                  {index < 1 && <div className="stage-arrow-connector">→</div>}
                </React.Fragment>
              ))}
            </div>
          </div>

          {activeLifecycleStage && <StageGroups stage={activeLifecycleStage} />}
        </section>

        {/* Gamified Broadcast Section */}
        <section className="page-section" id="talent-pool">
          <div className="gamified-broadcast-container">
            <div className="broadcast-header">
              <div className="broadcast-title-section">
                <h2 className="broadcast-main-title">Broadcast from Exodia HQ</h2>
                <p className="broadcast-subtitle">Stay updated with the latest news, articles, and announcements</p>
              </div>
              
              <div className="broadcast-controls">
                {bLoading && <div className="loading-indicator">🔄 Loading…</div>}
                {bErr && <div className="error-indicator">❌ Error: {bErr}</div>}
                <button
                  className="gamified-refresh-btn"
                  onClick={loadBroadcasts}
                  disabled={bLoading}
                  title="Refresh broadcasts"
                >
                  <span className="btn-icon">🔄</span>
                  <span className="btn-text">{bLoading ? 'Refreshing…' : 'Refresh'}</span>
                </button>
              </div>
            </div>

            <div className="gamified-broadcast-grid">
              {broadcasts.length > 0 ? (
                broadcasts.map(post => (
                  <div
                    key={post.id || post.ROWID || `${post.title}-${post.date || post.datetime || post.createdAt || Math.random()}`}
                    className={`gamified-broadcast-card ${String(post.type || '').toLowerCase()}`}
                    onClick={() => openBroadcastModal(post)}
                  >
                    <div className="broadcast-card-header">
                      <div className="broadcast-type-badge">
                        <span className="type-icon">{getBroadcastIcon(post.type)}</span>
                        <span className="type-text">{post.type || 'Announcement'}</span>
                      </div>
                      <div className="broadcast-date-badge">
                        <span className="date-icon">📅</span>
                        <span className="date-text">{displayDate(post)}</span>
                      </div>
                    </div>
                    <div className="broadcast-card-content">
                      <h3 className="broadcast-card-title">{post.title || 'Untitled'}</h3>
                      <p className="broadcast-card-description">{post.content || ''}</p>
                    </div>
                    <div className="broadcast-card-footer">
                      <div className="read-more-indicator">
                        <span>Click to read more</span>
                        <span className="arrow">→</span>
                      </div>
                    </div>
                    <div className="broadcast-card-glow"></div>
                  </div>
                ))
              ) : (!bLoading && !bErr) ? (
                <div className="no-broadcasts-message">
                  <div className="no-broadcasts-icon">📭</div>
                  <div className="no-broadcasts-text">No upcoming broadcasts.</div>
                  <div className="no-broadcasts-subtext">Only broadcasts from today or later are shown. Check back for new announcements!</div>
                </div>
              ) : null}
            </div>
          </div>

          {isBroadcastModalOpen && selectedBroadcast && (
            <div 
              className="broadcast-modal-overlay" 
              onClick={closeBroadcastModal}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                backdropFilter: 'blur(5px)'
              }}
            >
              <div 
                className="broadcast-modal" 
                onClick={e => e.stopPropagation()}
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: '20px',
                  padding: '2rem',
                  maxWidth: '600px',
                  width: '90%',
                  maxHeight: '80vh',
                  overflowY: 'auto',
                  color: 'white',
                  position: 'relative',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
                  border: '2px solid rgba(255,255,255,0.2)'
                }}
              >
                <button 
                  className="close-btn" 
                  onClick={closeBroadcastModal}
                  style={{
                    position: 'absolute',
                    top: '15px',
                    right: '15px',
                    background: 'rgba(255,255,255,0.2)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '40px',
                    height: '40px',
                    color: 'white',
                    fontSize: '20px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'rgba(255,255,255,0.3)';
                    e.target.style.transform = 'scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'rgba(255,255,255,0.2)';
                    e.target.style.transform = 'scale(1)';
                  }}
                >
                  ×
                </button>
                
                {shouldAutoOpenBroadcast(selectedBroadcast) && (
                  <div style={{
                    background: 'linear-gradient(135deg, #ff6b6b, #ff8e8e)',
                    color: 'white',
                    padding: '12px 20px',
                    borderRadius: '25px',
                    fontSize: '16px',
                    fontWeight: '700',
                    marginBottom: '20px',
                    textAlign: 'center',
                    animation: 'pulse 2s infinite',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    boxShadow: '0 4px 15px rgba(255, 107, 107, 0.4)'
                  }}>
                    Live Event - Happening Today!
                  </div>
                )}
                
                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                  <div style={{
                    fontSize: '3rem',
                    marginBottom: '10px',
                    filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))'
                  }}>
                    {getBroadcastIcon(selectedBroadcast.type)}
                  </div>
                  <h2 style={{ 
                    fontSize: '2rem', 
                    fontWeight: '700',
                    margin: '0 0 10px 0',
                    textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                  }}>
                    {selectedBroadcast.title || 'Untitled'}
                  </h2>
                </div>
                
                <div style={{
                  background: 'rgba(255,255,255,0.1)',
                  borderRadius: '15px',
                  padding: '20px',
                  marginBottom: '20px',
                  backdropFilter: 'blur(10px)'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    marginBottom: '15px',
                    flexWrap: 'wrap',
                    gap: '10px'
                  }}>
                    <div style={{
                      background: 'rgba(255,255,255,0.2)',
                      padding: '8px 16px',
                      borderRadius: '20px',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}>
                      <strong>Type:</strong> {selectedBroadcast.type || 'Announcement'}
                    </div>
                    <div style={{
                      background: 'rgba(255,255,255,0.2)',
                      padding: '8px 16px',
                      borderRadius: '20px',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}>
                      <strong>Date:</strong> {displayDate(selectedBroadcast)}
                    </div>
                  </div>
                  
                  <div style={{ 
                    fontSize: '16px',
                    lineHeight: '1.6',
                    textAlign: 'left'
                  }}>
                    {selectedBroadcast.content || ''}
                  </div>
                </div>
                
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: '15px',
                  marginTop: '20px'
                }}>
                  <button
                    onClick={closeBroadcastModal}
                    style={{
                      background: 'rgba(255,255,255,0.2)',
                      border: '2px solid rgba(255,255,255,0.3)',
                      color: 'white',
                      padding: '12px 24px',
                      borderRadius: '25px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      textTransform: 'uppercase',
                      letterSpacing: '1px'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = 'rgba(255,255,255,0.3)';
                      e.target.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'rgba(255,255,255,0.2)';
                      e.target.style.transform = 'translateY(0)';
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </section>

        {/* Upcoming Events Calendar */}
        <section className="page-section" id="talent-pool">
          <div className="section-header">
            <h2 className="section-title">Upcoming Events</h2>
            <p className="section-subtitle">Click on any date to view events and join our celebrations</p>
          </div>
          <div className="events-calendar-container">
            <EventCalendar 
              onEventClick={handleEventClick} 
              onEmptyDateClick={handleEmptyDateClick} 
            />
          </div>
        </section>

        <ContactCustomerService />
      </main>

      {/* Modals */}
      <TalentModal talent={selectedTalent} isOpen={isModalOpen} onClose={closeModal} />
      <EventModal 
        events={selectedEvents} 
        date={selectedDate} 
        isOpen={isEventModalOpen} 
        onClose={closeEventModal} 
      />
      <NoEventModal 
        date={selectedDate} 
        isOpen={isNoEventModalOpen} 
        onClose={closeNoEventModal} 
      />
      <PreOnboardingModal isOpen={isPreOnboardingOpen} onClose={() => setIsPreOnboardingOpen(false)} plan="Free" defaultRole="2D Game Artist" onSubmit={(p) => console.log('PreOnboarding form submitted:', p)} />
      <OnboardingModal   isOpen={isOnboardingOpen}   onClose={() => setIsOnboardingOpen(false)} />
      <DeploymentModal   isOpen={isDeploymentOpen}   onClose={() => setIsDeploymentOpen(false)} />
      <OffboardingModal  isOpen={isOffboardingOpen}  onClose={() => setIsOffboardingOpen(false)} />
      <FullSuiteModal    isOpen={isFullSuiteOpen}    onClose={() => setIsFullSuiteOpen(false)} />
      <ProjectModal      isOpen={isProjectModalOpen} onClose={() => setIsProjectModalOpen(false)} />
      <ContactHQModal
        isOpen={isContactHQOpen}
        onClose={() => setIsContactHQOpen(false)}
        onSubmit={(payload) => console.log('ContactHQ form submitted:', payload)}
      />

      {/* Gamified Verification Prompt Modal */}
      {showVerificationPrompt && (
        <div 
          className="gamified-verification-prompt-overlay" 
          onClick={() => setShowVerificationPrompt(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(5px)',
            animation: 'fadeIn 0.3s ease-out'
          }}
        >
          <div 
            className="gamified-verification-prompt" 
            onClick={e => e.stopPropagation()}
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '20px',
              padding: '2rem',
              maxWidth: '450px',
              width: '90%',
              color: 'white',
              position: 'relative',
              boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
              border: '2px solid rgba(255,255,255,0.2)',
              textAlign: 'center',
              animation: 'slideInUp 0.4s ease-out',
              transform: 'scale(1)',
              transition: 'all 0.3s ease'
            }}
          >
            <button 
              className="close-btn" 
              onClick={() => setShowVerificationPrompt(false)}
              style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                borderRadius: '50%',
                width: '35px',
                height: '35px',
                color: 'white',
                fontSize: '18px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(255,255,255,0.3)';
                e.target.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(255,255,255,0.2)';
                e.target.style.transform = 'scale(1)';
              }}
            >
              ×
            </button>
            
            <div style={{ fontSize: '3rem', marginBottom: '15px', animation: 'bounce 2s infinite' }}>
              {userData.isLoading ? '⏳' : '🔐'}
            </div>
            
            <h2 style={{ 
              fontSize: '1.5rem', 
              fontWeight: '700',
              margin: '0 0 15px 0',
              textShadow: '0 2px 4px rgba(0,0,0,0.3)'
            }}>
              {userData.isLoading ? 'Checking Status...' : 'Verification Required'}
            </h2>
            
            <div style={{
              background: 'rgba(255,255,255,0.1)',
              borderRadius: '15px',
              padding: '15px',
              marginBottom: '20px',
              backdropFilter: 'blur(10px)'
            }}>
              <p style={{ 
                fontSize: '14px',
                lineHeight: '1.5',
                margin: '0'
              }}>
                {userData.isLoading 
                  ? 'Please wait while we check your verification status...'
                  : `To ${verificationAction?.name}, you need to complete account verification first.`
                }
              </p>
            </div>
            
            {!userData.isLoading && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '12px',
                marginTop: '20px',
                flexWrap: 'wrap'
              }}>
                <button
                  onClick={() => {
                    setShowVerificationPrompt(false);
                    navigate('/profile');
                    setTimeout(() => {
                      setShowVerificationModal(true);
                    }, 100);
                  }}
                  style={{
                    background: 'linear-gradient(135deg, #ff6b6b, #ff8e8e)',
                    border: 'none',
                    color: 'white',
                    padding: '10px 20px',
                    borderRadius: '25px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    boxShadow: '0 4px 15px rgba(255, 107, 107, 0.4)',
                    minWidth: '120px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 20px rgba(255, 107, 107, 0.6)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 15px rgba(255, 107, 107, 0.4)';
                  }}
                >
                  Start Verification
                </button>
                
                <button
                  onClick={() => setShowVerificationPrompt(false)}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: '2px solid rgba(255,255,255,0.3)',
                    color: 'white',
                    padding: '10px 20px',
                    borderRadius: '25px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    minWidth: '120px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'rgba(255,255,255,0.3)';
                    e.target.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'rgba(255,255,255,0.2)';
                    e.target.style.transform = 'translateY(0)';
                  }}
                >
                  Maybe Later
                </button>
              </div>
            )}
            
            {userData.isLoading && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginTop: '20px',
                gap: '10px'
              }}>
                <div style={{
                  width: '20px',
                  height: '20px',
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderTop: '2px solid white',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                <span style={{ fontSize: '14px', opacity: '0.8' }}>Loading...</span>
              </div>
            )}
            
            {/* Glow effect */}
            <div style={{
              position: 'absolute',
              top: '-2px',
              left: '-2px',
              right: '-2px',
              bottom: '-2px',
              background: 'linear-gradient(135deg, #667eea, #764ba2, #ff6b6b)',
              borderRadius: '22px',
              zIndex: -1,
              opacity: '0.3',
              animation: 'glow 2s ease-in-out infinite alternate'
            }}></div>
          </div>
        </div>
      )}

      {/* Verification Modal */}
      {showVerificationModal && (
        <div 
          className="verification-modal-overlay" 
          onClick={() => setShowVerificationModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(5px)'
          }}
        >
          <div 
            className="verification-modal" 
            onClick={e => e.stopPropagation()}
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '20px',
              padding: '2rem',
              maxWidth: '500px',
              width: '90%',
              color: 'white',
              position: 'relative',
              boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
              border: '2px solid rgba(255,255,255,0.2)',
              textAlign: 'center'
            }}
          >
            <button 
              className="close-btn" 
              onClick={() => setShowVerificationModal(false)}
              style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                color: 'white',
                fontSize: '20px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease'
              }}
            >
              ×
            </button>
            
            <div style={{ fontSize: '4rem', marginBottom: '20px' }}>
              🔐
            </div>
            
            <h2 style={{ 
              fontSize: '1.8rem', 
              fontWeight: '700',
              margin: '0 0 20px 0',
              textShadow: '0 2px 4px rgba(0,0,0,0.3)'
            }}>
              Account Verification Required
            </h2>
            
            <div style={{
              background: 'rgba(255,255,255,0.1)',
              borderRadius: '15px',
              padding: '20px',
              marginBottom: '20px',
              backdropFilter: 'blur(10px)'
            }}>
              <p style={{ 
                fontSize: '16px',
                lineHeight: '1.6',
                margin: '0'
              }}>
                To access premium features like Pre-Onboarding, Onboarding, and other services, 
                you need to complete your account verification first.
              </p>
            </div>
            
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '15px',
              marginTop: '20px',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={() => {
                  setShowVerificationModal(false);
                  navigate('/profile');
                }}
                style={{
                  background: 'linear-gradient(135deg, #ff6b6b, #ff8e8e)',
                  border: 'none',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '25px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  boxShadow: '0 4px 15px rgba(255, 107, 107, 0.4)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 20px rgba(255, 107, 107, 0.6)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 15px rgba(255, 107, 107, 0.4)';
                }}
              >
                Start Verification
              </button>
              
              <button
                onClick={() => setShowVerificationModal(false)}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: '2px solid rgba(255,255,255,0.3)',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '25px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(255,255,255,0.3)';
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(255,255,255,0.2)';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                Maybe Later
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Login Verification Modal */}
      {showLoginVerificationModal && (
        <div 
          className="login-verification-modal-overlay" 
          onClick={() => setShowLoginVerificationModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(10px)',
            animation: 'fadeIn 0.4s ease-out'
          }}
        >
          <div 
            className="login-verification-modal" 
            onClick={e => e.stopPropagation()}
            style={{
              background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
              borderRadius: '24px',
              padding: '40px',
              maxWidth: '600px',
              width: '90%',
              position: 'relative',
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.4)',
              border: '2px solid rgba(255, 255, 255, 0.1)',
              animation: 'slideInUp 0.4s ease-out',
              textAlign: 'center'
            }}
          >
            {/* Floating celebration particles */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              pointerEvents: 'none',
              overflow: 'hidden',
              borderRadius: '24px'
            }}>
              {Array.from({ length: 15 }).map((_, i) => (
                <span 
                  key={i} 
                  style={{ 
                    position: 'absolute',
                    width: '6px',
                    height: '6px',
                    background: 'linear-gradient(45deg, #ffd700, #ff6b6b, #4ecdc4, #45b7d1)',
                    borderRadius: '50%',
                    animation: 'loginVerificationFloat 4s infinite linear',
                    animationDelay: `${i * 0.2}s`,
                    left: `${Math.random() * 100}%`,
                    top: '100%'
                  }} 
                />
              ))}
            </div>

            <div style={{ marginBottom: '30px' }}>
              <div style={{ 
                fontSize: '64px', 
                marginBottom: '20px',
                animation: 'bounce 2s infinite'
              }}>
                🎯
              </div>
              <h2 style={{ 
                color: '#ffffff',
                fontSize: '32px',
                fontWeight: '700',
                margin: '0 0 15px 0',
                textShadow: '0 3px 6px rgba(0, 0, 0, 0.3)',
                background: 'linear-gradient(135deg, #ffd700, #ff6b6b)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                Welcome to ExodiaUno!
              </h2>
              <p style={{ 
                color: '#b8c5d6',
                fontSize: '18px',
                margin: '0 0 20px 0',
                lineHeight: '1.4'
              }}>
                Your account has been created successfully!
              </p>
            </div>

            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '16px',
              padding: '25px',
              marginBottom: '30px',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <h3 style={{
                color: '#ffffff',
                fontSize: '20px',
                fontWeight: '600',
                margin: '0 0 20px 0'
              }}>
                Complete Your Account Verification
              </h3>
              <p style={{
                color: '#e2e8f0',
                fontSize: '16px',
                lineHeight: '1.6',
                margin: '0 0 20px 0'
              }}>
                To access all premium features like talent pool, role deck, and hiring tools, 
                you need to complete your account verification.
              </p>
              
              <div style={{
                background: 'rgba(74, 222, 128, 0.1)',
                border: '1px solid rgba(74, 222, 128, 0.2)',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '20px'
              }}>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
              <button
                onClick={() => setShowLoginVerificationModal(false)}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: '#b8c5d6',
                  padding: '15px 30px',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '600',
                  transition: 'all 0.2s ease',
                  minWidth: '120px'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                  e.target.style.color = '#ffffff';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                  e.target.style.color = '#b8c5d6';
                }}
              >
                Maybe Later
              </button>
              <button
                onClick={() => {
                  setShowLoginVerificationModal(false);
                  // Navigate to profile page with verification tab
                  window.location.href = '/profile?tab=profile';
                }}
                style={{
                  background: 'linear-gradient(135deg, #4ade80 0%, #22c55e 100%)',
                  border: 'none',
                  color: '#ffffff',
                  padding: '15px 30px',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '600',
                  boxShadow: '0 4px 12px rgba(74, 222, 128, 0.3)',
                  transition: 'all 0.3s ease',
                  minWidth: '150px'
                }}
                onMouseOver={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 20px rgba(74, 222, 128, 0.4)';
                }}
                onMouseOut={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(74, 222, 128, 0.3)';
                }}
              >
                Verify Account Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Login Verification Modal */}
      {showLoginVerificationModal && (
        <div 
          className="login-verification-overlay" 
          onClick={() => setShowLoginVerificationModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            backdropFilter: 'blur(10px)',
            animation: 'fadeIn 0.4s ease-out'
          }}
        >
          <div 
            className="login-verification-modal" 
            onClick={e => e.stopPropagation()}
            style={{
              background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
              borderRadius: '24px',
              padding: '40px',
              maxWidth: '600px',
              width: '90%',
              textAlign: 'center',
              color: 'white',
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.4)',
              border: '2px solid rgba(255, 255, 255, 0.1)',
              animation: 'slideInUp 0.4s ease-out'
            }}
          >
            {/* Floating celebration particles */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              pointerEvents: 'none',
              overflow: 'hidden',
              borderRadius: '24px'
            }}>
              {Array.from({ length: 15 }).map((_, i) => (
                <span 
                  key={i} 
                  style={{ 
                    position: 'absolute',
                    width: '6px',
                    height: '6px',
                    background: 'linear-gradient(45deg, #ffd700, #ff6b6b, #4ecdc4, #45b7d1)',
                    borderRadius: '50%',
                    animation: `float 3s infinite linear`,
                    animationDelay: `${i * 0.2}s`,
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`
                  }} 
                />
              ))}
            </div>

            <div style={{ marginBottom: '30px', position: 'relative' }}>
              <div style={{ 
                fontSize: '64px', 
                marginBottom: '20px',
                animation: 'bounce 2s infinite'
              }}>
                🎯
              </div>
              <h2 style={{ 
                fontSize: '32px', 
                fontWeight: '700', 
                margin: '0 0 15px 0',
                textShadow: '0 3px 6px rgba(0, 0, 0, 0.3)',
                background: 'linear-gradient(135deg, #ffd700, #ff6b6b)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                Welcome to ExodiaUno!
              </h2>
              <p style={{ 
                color: '#b8c5d6', 
                fontSize: '18px', 
                lineHeight: '1.6',
                margin: '0 0 20px 0'
              }}>
                Complete your account verification to unlock features!
              </p>
            </div>

            <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
              <button
                onClick={() => setShowLoginVerificationModal(false)}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: '#b8c5d6',
                  padding: '15px 30px',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '600',
                  transition: 'all 0.3s ease',
                  minWidth: '150px'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                  e.target.style.color = '#ffffff';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                  e.target.style.color = '#b8c5d6';
                }}
              >
                Maybe Later
              </button>
              <button
                onClick={() => {
                  setShowLoginVerificationModal(false);
                  // Navigate to profile page with verification tab
                  window.location.href = '#/profile?tab=profile';
                }}
                style={{
                  background: 'linear-gradient(135deg, #4ade80 0%, #22c55e 100%)',
                  border: 'none',
                  color: '#ffffff',
                  padding: '15px 30px',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '600',
                  boxShadow: '0 4px 12px rgba(74, 222, 128, 0.3)',
                  transition: 'all 0.3s ease',
                  minWidth: '200px'
                }}
                onMouseOver={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 20px rgba(74, 222, 128, 0.4)';
                }}
                onMouseOut={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(74, 222, 128, 0.3)';
                }}
              >
                Verify Account Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default HomePage;
